/**
 * useJulietteVoice — Orchestrateur de la conversation hands-free Juliette V3.5 (PR3).
 *
 * Pipeline d'un tour :
 *   1. VAD detecte speechStart → state="listening" (l'agent parle)
 *   2. VAD detecte speechEnd avec audio Float32Array
 *   3. → state="thinking"
 *   4. Encode WAV → POST /juliette-stt → transcript
 *   5. POST /juliette-turn-process → assistant_message
 *   6. → state="speaking"
 *   7. POST /juliette-tts-generate (modèle flash, MP3 base64 direct) → play audio
 *   8. Pendant TTS : pauseMic (anti-feedback)
 *   9. audio.ended → resumeMic, retour state="listening"
 *
 * Fenêtre active / veille :
 *   - Après silenceTimeoutMs sans parole → state="standby" (micro coupé, volet ouvert).
 *   - En veille, la parole ambiante est ignorée (pas de tour STT).
 *   - resume() (clic sur l'orbe) rouvre la fenêtre active depuis la veille.
 *   - Sur mobile : standbyAutoCloseMs > 0 → auto-ferme le volet depuis la veille.
 *   - Sur PC : standbyAutoCloseMs absent → reste en veille indéfiniment.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { extractSentences } from "@/lib/juliette-sentences";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useVAD } from "@/hooks/useVAD";
import { classifyMicError, type MicIssue } from "@/lib/mic-errors";
import { hasRealWord } from "@/lib/juliette-voice-filters";

export type VoiceState = "idle" | "listening" | "thinking" | "speaking" | "standby" | "error";

interface UseJulietteVoiceOptions {
  sessionId: string | null;
  /** Auto-close si silence prolonge apres un tour (ms). Defaut 10s. */
  silenceTimeoutMs?: number;
  /**
   * Si défini (>0), auto-ferme depuis la veille après ce délai (mobile).
   * PC : laisser undefined.
   */
  standbyAutoCloseMs?: number;
  /**
   * Le serveur a route le tour vers un AUTRE fil (fils par client) : le parent
   * doit basculer sessionId pour que l'onglet actif et les messages suivent.
   */
  onSessionRouted?: (sessionId: string) => void;
  /**
   * Appele au moment EXACT ou un segment commence a etre lu, avec son texte
   * brut (marqueurs [GOTO:] inclus). La file de lecture est la seule horloge
   * fiable : c'est ce qui permet au parent de changer d'ecran pile quand la
   * voix commence a le decrire (mode demo, plusieurs ecrans par reponse).
   */
  onSegmentStart?: (segment: string) => void;
  onClose?: () => void;
}

interface TurnHistory {
  user?: string;
  assistant?: string;
  /** Id du message assistant persiste — sert au feedback 👍/👎. */
  assistantMessageId?: string;
}

export interface ToolCallTrace {
  name: string;
  args: Record<string, unknown>;
  result: unknown;
}

interface UseJulietteVoiceResult {
  state: VoiceState;
  error: string | null;
  /**
   * Erreur d'accès micro traduite en message humain (null si pas d'erreur micro).
   * Distincte de `error` (brut, sert aux erreurs STT/TTS/turn). Sert à afficher
   * une carte « comment autoriser ton micro » + repli mode écrit.
   */
  micIssue: MicIssue | null;
  /** Historique des tours pour affichage discret. */
  transcript: TurnHistory[];
  /** Tools executes au dernier tour — sert au feedback visuel "j'ai bien note X". */
  lastToolCalls: ToolCallTrace[];
  /**
   * Demarre la session vocale. Avec `briefingDeck` (file d'actions affichee),
   * Juliette deroule le briefing au lieu de dire "je t'ecoute".
   */
  start: (opts?: { briefingDeck?: string }) => Promise<void>;
  /** Ferme la session (coupe le micro + declenche onClose). */
  stop: () => void;
  /** Coupe le micro SANS fermer la session (bascule voix->texte, changement de thread). */
  pause: () => void;
  /** Reprend l'écoute active depuis la veille (clic sur l'orbe). No-op hors veille. */
  resume: () => void;
}

// ─── WAV encoding helper ──────────────────────────────────────────────────
// Convertit Float32Array (16 kHz mono PCM, range [-1, 1]) en WAV PCM 16-bit
// puis base64. Format attendu par Voxtral via OpenRouter.
function float32ToWav(samples: Float32Array, sampleRate = 16000): Uint8Array {
  const numFrames = samples.length;
  const dataSize = numFrames * 2; // 16-bit per sample
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // "RIFF" header
  view.setUint32(0, 0x52494646, false); // "RIFF"
  view.setUint32(4, 36 + dataSize, true); // chunk size
  view.setUint32(8, 0x57415645, false); // "WAVE"
  // "fmt " sub-chunk
  view.setUint32(12, 0x666d7420, false); // "fmt "
  view.setUint32(16, 16, true); // PCM = 16 bytes
  view.setUint16(20, 1, true); // format = 1 (PCM)
  view.setUint16(22, 1, true); // channels = 1
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  // "data" sub-chunk
  view.setUint32(36, 0x64617461, false); // "data"
  view.setUint32(40, dataSize, true);

  // PCM samples
  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return new Uint8Array(buffer);
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Safari/iOS refuse les data: URLs longues sur un <audio> ("Failed to load
// because no supported source was found"). On decode le base64 en Blob et on
// sert un object URL — fiable cross-browser. Le caller doit revoquer l'URL.
function base64ToBlobUrl(b64: string, mime = "audio/mpeg"): string {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

// Le decoupage vit dans src/lib/juliette-sentences.ts (extrait le 2026-08-10
// pour etre testable) : seuil bas sur le premier segment (reactivite), eleve
// sur les suivants (ligne melodique). Voir l'en-tete du module pour le pourquoi.

// Pause avant de parler quand un segment ouvre un ecran ([GOTO:]) : l'ecran
// apparait, un souffle, puis la voix. Donne son rythme a la visite guidee.
const ACT_PAUSE_MS = 700;

// Greetings courts (≤ 3 mots) joues a l'ouverture de la fullscreen voice.
// Variabilite selon l'heure pour eviter l'effet robotique de la phrase fixe.
// Cache cote serveur efficace (voice_turn hash texte → chaque variante = 1 fichier
// reutilise apres premiere generation).
// Micro-accueil joue avant l'ouverture du micro : assez chaleureux et audible
// (les phrases trop seches type "Oui ?" passaient inapercues). Le vrai message
// d'accueil personnalise (prenom + meteo) est genere ensuite par le modele.
const GREETINGS_MORNING = ["Salut, je t'écoute !", "Coucou, ça va ?", "Bonjour ! Dis-moi tout.", "Hello, je suis là."];
const GREETINGS_DAY = ["Je t'écoute, dis-moi.", "Vas-y, je suis là.", "Oui, je t'écoute.", "Coucou, dis-moi tout."];
const GREETINGS_EVENING = ["Salut, je t'écoute.", "Vas-y, dis-moi tout.", "Bonsoir ! Je suis là.", "Je t'écoute, vas-y."];
const GREETINGS_NIGHT = ["Je t'écoute, dis-moi.", "Vas-y, je suis là.", "Bonsoir, dis-moi tout.", "Oui, je t'écoute."];

function pickGreeting(): string {
  const h = new Date().getHours();
  let bucket: string[];
  if (h >= 5 && h < 12) bucket = GREETINGS_MORNING;
  else if (h >= 12 && h < 18) bucket = GREETINGS_DAY;
  else if (h >= 18 && h < 22) bucket = GREETINGS_EVENING;
  else bucket = GREETINGS_NIGHT;
  return bucket[Math.floor(Math.random() * bucket.length)];
}

// supabase-js masque le body d'erreur derriere "Edge Function returned a non-2xx
// status code". Le vrai detail est dans error.context (Response). On le lit pour
// remonter le code+details exact (ex: "elevenlabs_error: ..." ou
// "daily_quota_exceeded") au lieu du message generique.
async function extractInvokeErrorMessage(
  err: unknown,
  fallbackPrefix: string,
): Promise<string> {
  if (!err || typeof err !== "object") {
    return `${fallbackPrefix}: ${String(err)}`;
  }
  const baseMsg = (err as { message?: string }).message ?? "unknown";
  const ctx = (err as { context?: unknown }).context;
  if (ctx instanceof Response) {
    try {
      const body = await ctx.clone().json();
      if (body && typeof body === "object") {
        const parts: string[] = [];
        const code = (body as { error?: string }).error;
        const details = (body as { details?: string }).details;
        if (typeof code === "string" && code.length > 0) parts.push(code);
        if (typeof details === "string" && details.length > 0) parts.push(details);
        if (parts.length > 0) return `${fallbackPrefix}: ${parts.join(" — ")}`;
      }
    } catch {
      // body pas JSON → fallback sur le message generique
    }
  }
  return `${fallbackPrefix}: ${baseMsg}`;
}

export function useJulietteVoice({
  sessionId,
  silenceTimeoutMs = 10000,
  standbyAutoCloseMs,
  onSessionRouted,
  onSegmentStart,
  onClose,
}: UseJulietteVoiceOptions): UseJulietteVoiceResult {
  // Route courante transmise au backend pour que Juliette sache sur quelle page est l'agent.
  const { pathname } = useLocation();
  const [state, setState] = useState<VoiceState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [micIssue, setMicIssue] = useState<MicIssue | null>(null);
  const [transcript, setTranscript] = useState<TurnHistory[]>([]);
  const [lastToolCalls, setLastToolCalls] = useState<ToolCallTrace[]>([]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  // Ref pour eviter la valeur perimee dans les closures de la file de lecture
  // (speakSentence vit dans un useCallback dont onSegmentStart n'est pas une dep).
  const onSegmentStartRef = useRef(onSegmentStart);
  onSegmentStartRef.current = onSegmentStart;
  const silenceTimerRef = useRef<number | null>(null);
  // Timer d'auto-fermeture depuis la veille (mobile uniquement).
  const standbyCloseTimerRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  const stateRef = useRef<VoiceState>("idle");
  // Compteur de tours "bidon" consecutifs (vide / sans vrai mot / audio trop court).
  // 2 d'affilee -> veille, pour ne pas boucler sur des hallucinations STT du silence.
  const junkCountRef = useRef(0);

  // Revoque l'object URL audio courant (anti-leak avant d'en creer un nouveau
  // ou au teardown).
  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const setVoiceState = useCallback((s: VoiceState) => {
    stateRef.current = s;
    setState(s);
  }, []);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearStandbyCloseTimer = useCallback(() => {
    if (standbyCloseTimerRef.current !== null) {
      window.clearTimeout(standbyCloseTimerRef.current);
      standbyCloseTimerRef.current = null;
    }
  }, []);

  // Bascule en VEILLE : micro coupé (on n'écoute plus le bruit ambiant), volet
  // ouvert. Reprise par resume() (clic) ou, plus tard, par mot de réveil. Sur
  // mobile, on arme une auto-fermeture. Réutilisé par le timer de silence ET le
  // coupe-boucle anti-hallucination.
  const enterStandby = useCallback(() => {
    clearSilenceTimer();
    setVoiceState("standby");
    vad.pauseMic();
    clearStandbyCloseTimer();
    if (standbyAutoCloseMs && standbyAutoCloseMs > 0) {
      standbyCloseTimerRef.current = window.setTimeout(() => {
        if (!stoppedRef.current) stop();
      }, standbyAutoCloseMs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSilenceTimer, clearStandbyCloseTimer, standbyAutoCloseMs, setVoiceState]);

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = window.setTimeout(() => {
      if (stoppedRef.current) return;
      // On ne bascule en veille que depuis l'écoute active — jamais en plein tour (thinking/speaking).
      if (stateRef.current !== "listening") return;
      // Fenêtre active expirée → VEILLE.
      enterStandby();
    }, silenceTimeoutMs);
  }, [clearSilenceTimer, silenceTimeoutMs, enterStandby]);

  /**
   * Joue UN tour parle : appel SSE a juliette-turn-process, decoupe du flux en
   * phrases, lecture TTS sequentielle, puis reouverture du micro.
   *
   * Partage par les deux entrees : un tour de conversation (apres STT) ET le
   * briefing du dashboard (premier tour, Juliette prend la parole d'elle-meme).
   * Leve en cas d'echec — l'appelant gere l'etat d'erreur.
   */
  const runTurnAndSpeak = useCallback(
    async (payload: { userMessage: string; isFirstTurn?: boolean; deck?: string }) => {
      // Sans message utilisateur (briefing), on cree le creneau de transcript
      // que les deltas assistant viendront remplir.
      if (!payload.userMessage) setTranscript((prev) => [...prev, {}]);

      // On reste en "thinking" tant que le LLM n'a pas produit de texte (il peut
      // d'abord appeler des outils) ; on bascule "speaking" + coupe le mic au
      // 1er fragment, juste avant le 1er audio.
      let startedSpeaking = false;

      // Lit une phrase : TTS (modele flash) → Blob → lecture, en attendant la fin.
      // Les erreurs d'une phrase sont avalees pour ne pas casser la chaine.
      const speakSentence = (sentence: string, previous: string): Promise<void> =>
        (async () => {
          if (stoppedRef.current || !sentence.trim()) return;
          const ttsRes = await supabase.functions.invoke("juliette-tts-generate", {
            body: {
              text: sentence,
              context: "voice_turn",
              session_id: sessionId,
              // Segment precedent : sans lui, chaque segment repart d'une
              // intonation neutre et se termine comme une fin de paragraphe.
              // Contexte seul, non synthetise et non facture cote log.
              previous_text: previous,
            },
          });
          if (ttsRes.error) throw new Error(await extractInvokeErrorMessage(ttsRes.error, "TTS"));
          const ttsData = ttsRes.data as { audio_base64?: string };
          if (!ttsData?.audio_base64 || stoppedRef.current) return;

          // Respiration de presentateur : un segment qui porte un [GOTO:] ouvre
          // un ecran. On navigue D'ABORD, on laisse un temps a l'oeil pour voir
          // la page apparaitre, PUIS la voix reprend. Sans ce battement, la
          // visite guidee (mode demo) s'ecoule d'un bloc et devient monotone.
          const opensScreen = /\[GOTO:/i.test(sentence);
          if (opensScreen) {
            onSegmentStartRef.current?.(sentence);
            await new Promise((r) => setTimeout(r, ACT_PAUSE_MS));
            if (stoppedRef.current) return;
          }

          await new Promise<void>((resolve) => {
            const audioEl = audioRef.current ?? new Audio();
            audioRef.current = audioEl;
            revokeObjectUrl();
            objectUrlRef.current = base64ToBlobUrl(ttsData.audio_base64!);
            audioEl.src = objectUrlRef.current;
            audioEl.onended = () => resolve();
            audioEl.onerror = () => resolve(); // ne bloque pas la file
            // La voix de ce segment demarre MAINTENANT : le parent peut y
            // accrocher la navigation (l'ecran change quand elle en parle).
            if (!opensScreen) onSegmentStartRef.current?.(sentence);
            audioEl.play().catch(() => resolve());
          });
        })();

      // File de lecture sequentielle : chaque phrase est jouee dans l'ordre, la
      // suivante demarre quand la precedente est finie. Une phrase qui echoue
      // est loggee et ne stoppe pas la conversation.
      let speakChain: Promise<void> = Promise.resolve();
      // Dernier segment EMPILE (donc dans l'ordre du flux, pas de la lecture) :
      // sert de contexte prosodique au segment suivant.
      let previousSegment = "";
      const enqueueSentence = (sentence: string) => {
        const previous = previousSegment;
        previousSegment = sentence;
        speakChain = speakChain
          .then(() => (stoppedRef.current ? undefined : speakSentence(sentence, previous)))
          .catch((e) => {
            console.error("[juliette-voice] phrase non lue :", e);
          });
      };

      // Appel SSE direct (fetch + reader) — supabase.functions.invoke ne streame pas.
      const {
        data: { session: authSession },
      } = await supabase.auth.getSession();
      const accessToken = authSession?.access_token;
      if (!accessToken) throw new Error("session_missing");
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/juliette-turn-process`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          user_message: payload.userMessage,
          is_first_turn: payload.isFirstTurn === true,
          stream: true,
          // Ce front sait suivre une bascule de fil (onglets par client).
          supports_thread_routing: true,
          client_context: {
            current_route: pathname,
            // File d'actions EXACTE affichee a l'ecran (briefing dashboard).
            ...(payload.deck ? { dashboard_deck: payload.deck } : {}),
          },
        }),
      });
      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => "");
        throw new Error(`Turn HTTP ${resp.status} : ${errText.slice(0, 200)}`);
      }

      // Met a jour l'entree du tour courant au fil des deltas — meme pairing
      // user/assistant que le mode non-streame.
      const updateAssistantTranscript = (text: string, id?: string) => {
        setTranscript((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last) {
            last.assistant = text;
            if (id) last.assistantMessageId = id;
          }
          return next;
        });
      };

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let fullAssistant = "";
      let sentenceBuf = "";
      // Vrai des qu'un segment est parti : relache le seuil de decoupage pour
      // les suivants (le premier son est deja arrive, on peut viser plus long).
      let sentSomething = false;
      let streamError: string | null = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nlIdx: number;
        while ((nlIdx = buf.indexOf("\n")) !== -1) {
          const line = buf.slice(0, nlIdx).trim();
          buf = buf.slice(nlIdx + 1);
          if (!line.startsWith("data:")) continue;
          const rawEvt = line.slice(5).trim();
          if (!rawEvt) continue;
          let evt: {
            type?: string;
            text?: string;
            session_id?: string | null;
            assistant_message_id?: string | null;
            tool_calls_executed?: ToolCallTrace[];
            message?: string;
          };
          try {
            evt = JSON.parse(rawEvt);
          } catch {
            continue;
          }
          if (evt.type === "delta" && typeof evt.text === "string") {
            if (!startedSpeaking) {
              startedSpeaking = true;
              setVoiceState("speaking");
              vad.pauseMic(); // anti-feedback : mic coupe avant le 1er audio
            }
            fullAssistant += evt.text;
            sentenceBuf += evt.text;
            const { ready, rest } = extractSentences(sentenceBuf, !sentSomething);
            sentenceBuf = rest;
            if (ready.length > 0) sentSomething = true;
            for (const s of ready) enqueueSentence(s);
            updateAssistantTranscript(fullAssistant);
          } else if (evt.type === "done") {
            if (evt.assistant_message_id) {
              updateAssistantTranscript(fullAssistant, evt.assistant_message_id);
            }
            setLastToolCalls(
              Array.isArray(evt.tool_calls_executed) ? evt.tool_calls_executed : [],
            );
            // Routage par contact : le serveur a fait le tour dans un autre fil
            // (celui du client cite) — le parent bascule sessionId pour que les
            // prochains tours partent dans le bon fil.
            if (typeof evt.session_id === "string" && evt.session_id && evt.session_id !== sessionId) {
              onSessionRouted?.(evt.session_id);
            }
          } else if (evt.type === "error") {
            streamError = evt.message ?? "stream_error";
          }
        }
      }
      if (streamError) throw new Error(streamError);

      // Flush du reste (derniere phrase sans terminateur) puis attente de la fin
      // de lecture de toutes les phrases avant de rouvrir le mic.
      if (sentenceBuf.trim()) enqueueSentence(sentenceBuf.trim());
      await speakChain;

      if (stoppedRef.current) return;
      setVoiceState("listening");
      vad.resumeMic();
      armSilenceTimer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, pathname, armSilenceTimer, setVoiceState, revokeObjectUrl, onSessionRouted],
  );

  const handleSpeechEnd = useCallback(
    async (audio: Float32Array) => {
      if (stoppedRef.current || !sessionId) return;
      // En veille on n'écoute pas le bruit ambiant : seul resume() (clic) — ou plus
      // tard le mot de réveil — relance l'écoute active.
      if (stateRef.current === "standby") return;
      clearSilenceTimer();

      // Tour "bidon" (vide / sans vrai mot / audio trop court = bruit ambiant) : on
      // NE l'envoie PAS a Juliette. 2 d'affilee -> veille (on remet le comportement
      // "pas de parole = on se tait", cassé depuis le passage a gpt-4o-transcribe qui
      // hallucine sur du silence). Sinon on relance juste l'ecoute active.
      const rejectPhantomTurn = () => {
        if (stoppedRef.current) return;
        if ((junkCountRef.current += 1) >= 2) {
          enterStandby();
        } else {
          setVoiceState("listening");
          vad.resumeMic();
          armSilenceTimer();
        }
      };

      // #4 Garde durée mini : une rafale < 0,4 s est du bruit, pas de la parole.
      //    On ne paie pas le STT et on compte comme un tour bidon.
      if (audio.length / 16000 < 0.4) {
        rejectPhantomTurn();
        return;
      }

      setVoiceState("thinking");

      try {
        // 1. Encode WAV + base64
        const wav = float32ToWav(audio, 16000);
        const audioB64 = uint8ToBase64(wav);

        // 2. STT
        const sttRes = await supabase.functions.invoke("juliette-stt", {
          body: { audio: audioB64, format: "wav" },
        });
        if (sttRes.error) throw new Error(await extractInvokeErrorMessage(sttRes.error, "STT"));
        const userMsg = (sttRes.data as { transcript?: string })?.transcript?.trim() ?? "";
        // #1 Filtre "vrai mot" : vide OU sans aucun vrai mot ("###", '""', "...") =
        //    hallucination du STT sur du silence. On rejette comme un tour bidon.
        if (!userMsg || !hasRealWord(userMsg)) {
          rejectPhantomTurn();
          return;
        }
        // Vrai tour utilisateur : on remet le compteur de bidons a zero.
        junkCountRef.current = 0;
        setTranscript((prev) => [...prev, { user: userMsg }]);

        // 3. Tour + parole (extrait dans runTurnAndSpeak, partage avec le briefing).
        await runTurnAndSpeak({ userMessage: userMsg });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setVoiceState("error");
        vad.resumeMic();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, runTurnAndSpeak, armSilenceTimer, enterStandby, clearSilenceTimer, setVoiceState],
  );

  const vad = useVAD({
    onSpeechStart: () => {
      if (stateRef.current === "listening") {
        clearSilenceTimer();
      }
    },
    onSpeechEnd: handleSpeechEnd,
    onError: (e) => {
      // Toute erreur remontée par le VAD = échec d'accès micro (getUserMedia de
      // MicVAD). On la traduit en message humain orienté action.
      setMicIssue(classifyMicError(e.message));
      setError(e.message);
      setVoiceState("error");
    },
  });

  const start = useCallback(async (opts?: { briefingDeck?: string }) => {
    if (!sessionId) {
      setError("session_id requis");
      setVoiceState("error");
      return;
    }
    stoppedRef.current = false;
    junkCountRef.current = 0;
    setError(null);
    setMicIssue(null);
    setTranscript([]);
    setLastToolCalls([]);

    // 1bis. Briefing dashboard : Juliette prend la parole d'elle-meme et deroule
    // la file d'actions affichee, AU LIEU du greeting court. Le micro s'ouvre
    // ensuite (runTurnAndSpeak fait resumeMic + armSilenceTimer en fin de tour).
    if (opts?.briefingDeck) {
      setVoiceState("speaking");
      try {
        await vad.start();
        vad.pauseMic(); // anti-feedback pendant qu'elle parle
        await runTurnAndSpeak({ userMessage: "", isFirstTurn: true, deck: opts.briefingDeck });
      } catch (err) {
        // Briefing en echec : on ne bloque pas la session, on ouvre le micro.
        console.error("[juliette-voice] briefing non joue :", err);
        if (!stoppedRef.current) {
          setVoiceState("listening");
          vad.resumeMic();
          armSilenceTimer();
        }
      }
      return;
    }

    // 1. Greeting court (≤ 3 mots, randomise selon l'heure) avant d'ouvrir le mic.
    // Mode degrade silencieux si le TTS plante : on bascule direct en listening.
    setVoiceState("speaking");
    try {
      const greeting = pickGreeting();
      const ttsRes = await supabase.functions.invoke("juliette-tts-generate", {
        body: { text: greeting, context: "greeting", session_id: sessionId },
      });
      if (!ttsRes.error) {
        const ttsData = ttsRes.data as { audio_base64?: string };
        if (ttsData?.audio_base64 && !stoppedRef.current) {
          const audioEl = audioRef.current ?? new Audio();
          audioRef.current = audioEl;
          revokeObjectUrl();
          objectUrlRef.current = base64ToBlobUrl(ttsData.audio_base64);
          audioEl.src = objectUrlRef.current;
          await new Promise<void>((resolve) => {
            const done = () => resolve();
            audioEl.onended = done;
            audioEl.onerror = done;
            audioEl.play().catch(done);
          });
        }
      }
    } catch {
      // greeting est best-effort — un echec ne doit pas bloquer la session.
    }

    if (stoppedRef.current) return;

    // 2. Ouvrir le mic, demarrer le tour de parole.
    setVoiceState("listening");
    await vad.start();
    armSilenceTimer();
  }, [sessionId, vad, armSilenceTimer, setVoiceState, revokeObjectUrl, runTurnAndSpeak]);

  // Coupe le micro + l'audio en cours, sans declencher onClose. start() reset
  // stoppedRef a false, donc on peut reprendre la voix ensuite.
  const pause = useCallback(() => {
    stoppedRef.current = true;
    clearSilenceTimer();
    clearStandbyCloseTimer();
    vad.stop();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    revokeObjectUrl();
    setVoiceState("idle");
  }, [vad, clearSilenceTimer, clearStandbyCloseTimer, setVoiceState, revokeObjectUrl]);

  const stop = useCallback(() => {
    pause();
    onClose?.();
  }, [pause, onClose]);

  const resume = useCallback(() => {
    if (stateRef.current !== "standby") return;
    clearStandbyCloseTimer();
    stoppedRef.current = false;
    junkCountRef.current = 0;
    setMicIssue(null);
    setVoiceState("listening");
    vad.resumeMic();
    armSilenceTimer();
  }, [vad, clearStandbyCloseTimer, setVoiceState, armSilenceTimer]);

  // Cleanup au demontage
  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      clearSilenceTimer();
      clearStandbyCloseTimer();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [clearSilenceTimer, clearStandbyCloseTimer]);

  return { state, error, micIssue, transcript, lastToolCalls, start, stop, pause, resume };
}
